/**
 * A campaign's placements: every location an out-of-home ad may run on, each
 * with the TabID its daily ad logs name it by, its market and its dates
 * (Confluence OP/3623026689, "OOH Digital - Ad Log Process"). PURE.
 *
 * A row without a TabID is refused: an ad log line names a placement only by
 * it. A TabID named twice keeps its first row.
 */
import type { SavedCampaignPlacement, SavedPlacement } from '../contracts/pql';
import { MAX_OOH_PLACEMENTS } from '../engine/request';
import {
    coordinateColumns,
    headerRowOf,
    radiusForType,
    RADIUS_HEADERS,
    TYPE_HEADERS,
} from '../ooh/placements';
import {
    colOf,
    END_HEADERS,
    isoDateOf,
    MARKET_HEADERS,
    START_HEADERS,
    TAB_ID_HEADERS,
} from './columns';

export interface ParsedCampaign {
    placements: SavedPlacement[];
    campaign: SavedCampaignPlacement[];
    refusals: string[];
    duplicates: number;
    /** The earliest start and latest end the rows name, for the flight. */
    flightStart?: string;
    flightEnd?: string;
}

/** True when a location table has a TabID column. */
export function hasTabIdColumn(rows: string[][]): boolean {
    const h = headerRowOf(rows);
    return h >= 0 && colOf(rows[h], TAB_ID_HEADERS) >= 0;
}

export function parseCampaignRows(rows: string[][]): ParsedCampaign {
    const h = headerRowOf(rows);
    const none = (why: string): ParsedCampaign => ({
        placements: [],
        campaign: [],
        refusals: [why],
        duplicates: 0,
    });
    if (h < 0)
        return none(
            'The file needs a latitude column (lat or latitude) and a longitude column (lng, lon or longitude).',
        );
    const headers = rows[h].map((c) => String(c ?? ''));
    const { lat: latCol, lng: lngCol } = coordinateColumns(rows, h);
    const tabCol = colOf(headers, TAB_ID_HEADERS);
    if (tabCol < 0)
        return none('The file needs a TabID column: the daily ad logs name each placement by it.');
    const typeCol = colOf(headers, TYPE_HEADERS);
    const radiusCol = colOf(headers, RADIUS_HEADERS);
    const marketCol = colOf(headers, MARKET_HEADERS);
    const startCol = colOf(headers, START_HEADERS);
    const endCol = colOf(headers, END_HEADERS);
    const out: ParsedCampaign = { placements: [], campaign: [], refusals: [], duplicates: 0 };
    const seen = new Set<string>();
    rows.slice(h + 1).forEach((row, i) => {
        const rowNo = h + i + 2;
        const cell = (c: number) => (c >= 0 ? String(row[c] ?? '').trim() : '');
        if (row.every((c) => !String(c ?? '').trim())) return;
        const tabId = cell(tabCol);
        if (!/^\d+$/.test(tabId)) {
            out.refusals.push(
                `Row ${rowNo}: ${tabId ? 'the TabID must be a number' : 'no TabID'}.`,
            );
            return;
        }
        const lat = Number(cell(latCol));
        const lng = Number(cell(lngCol));
        if (
            !Number.isFinite(lat) ||
            !Number.isFinite(lng) ||
            !cell(latCol) ||
            !cell(lngCol) ||
            Math.abs(lat) > 90 ||
            Math.abs(lng) > 180
        ) {
            out.refusals.push(`Row ${rowNo}: latitude and longitude must be numbers on the map.`);
            return;
        }
        if (seen.has(tabId)) {
            out.duplicates++;
            return;
        }
        seen.add(tabId);
        const type = cell(typeCol) || undefined;
        const stated = Number(cell(radiusCol));
        out.placements.push({
            lat,
            lng,
            radiusMeters:
                Number.isFinite(stated) && stated >= 10 && stated <= 1000
                    ? Math.round(stated)
                    : radiusForType(type),
            ...(type ? { type: type.slice(0, 80) } : {}),
        });
        const start = isoDateOf(cell(startCol)) ?? undefined;
        const end = isoDateOf(cell(endCol)) ?? undefined;
        out.campaign.push({
            tabId,
            ...(cell(marketCol) ? { market: cell(marketCol).slice(0, 80) } : {}),
            ...(start ? { start } : {}),
            ...(end ? { end } : {}),
        });
        if (start && (!out.flightStart || start < out.flightStart)) out.flightStart = start;
        if (end && (!out.flightEnd || end > out.flightEnd)) out.flightEnd = end;
    });
    if (out.placements.length > MAX_OOH_PLACEMENTS)
        return none(
            `${out.placements.length.toLocaleString('en-US')} placements is more than one campaign takes (${MAX_OOH_PLACEMENTS.toLocaleString('en-US')}).`,
        );
    return out;
}
